import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG = "https://images.unsplash.com/photo-1611323340350-bdcc0e6cfae5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

function MaskedLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className || ""}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Champagne glasses in a dimly lit luxury lounge"
          className="w-full h-[120%] object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-obsidian/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-32 pb-24 w-full">
        <motion.p
          data-testid="hero-eyebrow"
          className="text-xs uppercase tracking-[0.35em] text-gold/90 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.3, duration: 1 } }}
        >
          The Private Companionship Directory
        </motion.p>

        <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light text-amber-100/95 leading-[1.05] tracking-tight max-w-4xl">
          <MaskedLine delay={0.5}>Where discretion</MaskedLine>
          <MaskedLine delay={0.65}>meets <span className="italic gold-text-shimmer animate-shimmer">desire.</span></MaskedLine>
        </h1>

        <motion.p
          className="mt-8 max-w-xl text-base sm:text-lg text-neutral-300 font-light leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 1.1, duration: 0.8 } }}
        >
          Monroe Society is an elevated advertising platform for independent companions — and the
          discerning clientele who seek exceptional company for dinner, travel, and social engagements.
        </motion.p>

        <motion.div
          className="mt-12 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 1.3, duration: 0.8 } }}
        >
          <a
            href="#directory"
            data-testid="hero-explore-button"
            className="px-10 py-4 bg-gold text-obsidian text-sm uppercase tracking-[0.2em] font-medium text-center hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all duration-300"
          >
            Explore the Directory
          </a>
          <Link
            to="/join/provider"
            data-testid="hero-advertise-button"
            className="px-10 py-4 border hairline-gold text-amber-100/90 text-sm uppercase tracking-[0.2em] text-center hover:border-amber-400/60 hover:bg-amber-500/5 transition-all duration-300"
          >
            Advertise With Us
          </Link>
        </motion.div>

        <motion.p
          className="mt-16 text-xs text-neutral-500 tracking-wide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 1.6, duration: 1 } }}
        >
          Strictly 18+ · An advertising platform only — not an escort agency
        </motion.p>
      </motion.div>
    </section>
  );
}
